import React, { useState, useEffect } from 'react'
import { RefreshCw, Plus, Edit2, Trash2, Save, Calendar } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'

interface RotationGroup {
  id: string
  name: string
  description?: string
  chore_ids: string[]
  frequency: string
  distribution_strategy: string
  rotation_type: string
  active_kid_id?: string
  kid_ids?: string[]
  last_rotation?: string
  next_rotation?: string
  is_active: boolean
  auto_rotate: boolean
  rotation_order?: string[]
  rotation_days?: number
  start_date?: string
}

interface Chore {
  id: string
  name: string
  points: number
  category: string
}

interface Kid {
  id: string
  username: string
  display_name: string
  color_theme: string
}

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
]

export const RotationGroupsManager: React.FC = () => {
  const [groups, setGroups] = useState<RotationGroup[]>([])
  const [chores, setChores] = useState<Chore[]>([])
  const [kids, setKids] = useState<Kid[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddGroup, setShowAddGroup] = useState(false)
  const [editingGroup, setEditingGroup] = useState<RotationGroup | null>(null)
  const [selectedChores, setSelectedChores] = useState<string[]>([])
  const [selectedKids, setSelectedKids] = useState<string[]>([])
  const [choreSearch, setChoreSearch] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  
  const [newGroup, setNewGroup] = useState<Partial<RotationGroup>>({
    name: '',
    description: '',
    chore_ids: [],
    frequency: 'daily',
    distribution_strategy: 'fair_rotation',
    rotation_type: 'daily',
    is_active: true,
    auto_rotate: true,
    kid_ids: [],
    rotation_days: 1,
    start_date: format(new Date(), 'yyyy-MM-dd')
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [groupsData, choresData, usersData] = await Promise.all([
        apiClient.get('/api/rotation-groups?active_only=false'),
        apiClient.get('/api/chores'),
        apiClient.get('/api/users')
      ])

      // Defensive checks for Go backend wrapped response format
      const groupsArray = Array.isArray(groupsData) ? groupsData :
                          (groupsData?.rotation_groups && Array.isArray(groupsData.rotation_groups)) ? groupsData.rotation_groups : []
      const choresArray = Array.isArray(choresData) ? choresData :
                          (choresData?.chores && Array.isArray(choresData.chores)) ? choresData.chores : []
      const usersArray = Array.isArray(usersData) ? usersData :
                         (usersData?.users && Array.isArray(usersData.users)) ? usersData.users : []

      setGroups(groupsArray)
      setChores(choresArray)
      setKids(usersArray.filter((u: any) => !u.is_parent && u.is_active))
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load rotation groups')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateGroup = async () => {
    if (!newGroup.name || selectedChores.length === 0) {
      toast.error('Please provide a name and select at least one chore')
      return
    }

    try {
      await apiClient.post('/api/rotation-groups/create', {
        ...newGroup,
        chore_ids: selectedChores,
        kid_ids: selectedKids
      })
      toast.success('Rotation group created successfully')
      setShowAddGroup(false)
      setNewGroup({
        name: '',
        description: '',
        chore_ids: [],
        frequency: 'daily',
        distribution_strategy: 'fair_rotation',
        rotation_type: 'daily',
        is_active: true,
        auto_rotate: true,
        kid_ids: []
      })
      setSelectedChores([])
      setSelectedKids([])
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create rotation group')
    }
  }

  const handleUpdateGroup = async () => {
    if (!editingGroup) return

    try {
      await apiClient.patch(`/api/rotation-groups/${editingGroup.id}`, {
        name: editingGroup.name,
        description: editingGroup.description,
        frequency: editingGroup.frequency,
        distribution_strategy: editingGroup.distribution_strategy,
        is_active: editingGroup.is_active,
        auto_rotate: editingGroup.auto_rotate,
        start_date: editingGroup.start_date,
        rotation_days: editingGroup.rotation_days,
        chore_ids: selectedChores,
        kid_ids: selectedKids
      })
      toast.success('Rotation group updated successfully')
      setEditingGroup(null)
      setSelectedChores([])
      setSelectedKids([])
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to update rotation group')
    }
  }

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('Are you sure you want to delete this rotation group?')) return
    
    try {
      await apiClient.delete(`/api/rotation-groups/${groupId}`)
      toast.success('Rotation group deleted successfully')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to delete rotation group')
    }
  }

  const handleRotateNow = async (groupId: string) => {
    try {
      // Runs the rotation scheduler now so today's chores are handed out
      const res = await apiClient.post(`/api/rotation-groups/${groupId}/rotate`, {})
      const n = res?.assigned_today
      toast.success(typeof n === 'number' ? `Rotation updated: ${n} chore${n === 1 ? '' : 's'} assigned today` : 'Rotation updated')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to assign chores')
    }
  }

  const handleToggleActive = async (groupId: string, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/rotation-groups/${groupId}`, { is_active: !isActive })
      toast.success(`Rotation group ${!isActive ? 'activated' : 'deactivated'}`)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to update rotation group')
    }
  }

  const getFrequencyLabel = (freq: string) => {
    return FREQUENCIES.find(f => f.value === freq)?.label || freq
  }

  const getChoreNames = (choreIds: string[]) => {
    if (!choreIds || !Array.isArray(choreIds) || choreIds.length === 0) return 'No chores assigned'
    return choreIds.map(id => {
      const chore = chores.find(c => c.id === id)
      return chore ? chore.name : 'Unknown'
    }).join(', ')
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Rotation Groups</h2>
        <p className="text-gray-600">Manage automatic chore rotation assignments</p>
      </div>

      {/* Add Group Button */}
      <button
        onClick={() => setShowAddGroup(true)}
        className="mb-6 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Rotation Group
      </button>

      {/* Groups List */}
      <div className="space-y-4">
        {groups.map(group => {
          const frequencyLabel = getFrequencyLabel(group.frequency || group.rotation_type)
          
          return (
            <div 
              key={group.id} 
              className={`bg-white rounded-lg shadow p-6 ${!group.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center mb-2">
                    <div className={`p-2 rounded-lg bg-blue-100 mr-3`}>
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                      <p className="text-sm text-gray-500">{frequencyLabel} &middot; Fair rotation</p>
                    </div>
                  </div>
                  {group.description && (
                    <p className="text-gray-600 ml-12">{group.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleRotateNow(group.id)}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Rotate Now
                  </button>
                  <button
                    onClick={() => {
                      setEditingGroup(group)
                      setSelectedChores(group.chore_ids || [])
                      setSelectedKids(group.kid_ids || [])
                    }}
                    className="p-2 text-blue-600 hover:text-blue-900"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(group.id, group.is_active)}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      group.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {group.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(group.id)}
                    className="p-2 text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Chores in Rotation</h4>
                  <div className="text-sm text-gray-600">
                    {getChoreNames(group.chore_ids || [])}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Kids</h4>
                  <div className="text-sm text-gray-600">
                    {(group.kid_ids || []).map(id => kids.find(k => k.id === id)?.display_name).filter(Boolean).join(', ') || 'None'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Each chore goes to whoever has done it least lately.</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Rotation Schedule</h4>
                  <div className="space-y-1">
                    {group.last_rotation && (
                      <div className="text-sm text-gray-600">
                        Last: {format(parseISO(group.last_rotation), 'MMM d, h:mm a')}
                      </div>
                    )}
                    {group.next_rotation && (
                      <div className="text-sm text-gray-600">
                        Next: {format(parseISO(group.next_rotation), 'MMM d, h:mm a')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          )
        })}
      </div>

      {/* Add/Edit Group Modal */}
      {(showAddGroup || editingGroup) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingGroup ? 'Edit Rotation Group' : 'Add New Rotation Group'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Group Name *</label>
                <input
                  type="text"
                  value={editingGroup ? editingGroup.name : newGroup.name}
                  onChange={(e) => editingGroup 
                    ? setEditingGroup({ ...editingGroup, name: e.target.value })
                    : setNewGroup({ ...newGroup, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Kitchen Tasks"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editingGroup ? editingGroup.description || '' : newGroup.description}
                  onChange={(e) => editingGroup
                    ? setEditingGroup({ ...editingGroup, description: e.target.value })
                    : setNewGroup({ ...newGroup, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="Describe this rotation group..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Frequency *</label>
                  <select
                    value={editingGroup ? editingGroup.frequency : newGroup.frequency}
                    onChange={(e) => editingGroup
                      ? setEditingGroup({ ...editingGroup, frequency: e.target.value })
                      : setNewGroup({ ...newGroup, frequency: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Select Chores * ({selectedChores.length} selected)
                </label>
                <input
                  type="text"
                  value={choreSearch}
                  onChange={(e) => setChoreSearch(e.target.value)}
                  placeholder="Search chores..."
                  className="w-full px-3 py-2 border rounded-lg mb-2"
                />
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {Object.entries(
                    chores
                      .filter(c => !choreSearch || c.name.toLowerCase().includes(choreSearch.toLowerCase()))
                      .reduce((acc, chore) => {
                        const category = chore.category || 'Other'
                        if (!acc[category]) acc[category] = []
                        acc[category].push(chore)
                        return acc
                      }, {} as Record<string, Chore[]>)
                  ).sort(([a], [b]) => a.localeCompare(b)).map(([category, categoryChores]) => (
                    <div key={category}>
                      <button
                        type="button"
                        onClick={() => setExpandedCategories(prev => {
                          const next = new Set(prev)
                          next.has(category) ? next.delete(category) : next.add(category)
                          return next
                        })}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b"
                      >
                        <span className="text-xs font-semibold text-gray-600 uppercase">{category}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{categoryChores.filter(c => selectedChores.includes(c.id)).length}/{categoryChores.length}</span>
                          <span className="text-gray-400">{expandedCategories.has(category) ? '▼' : '▶'}</span>
                        </div>
                      </button>
                      {expandedCategories.has(category) && categoryChores.map(chore => (
                        <label key={chore.id} className="flex items-center px-3 py-1.5 cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedChores?.includes(chore.id) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedChores([...selectedChores, chore.id])
                              } else {
                                setSelectedChores(selectedChores.filter(id => id !== chore.id))
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm flex-1">{chore.name}</span>
                          <span className="text-xs text-gray-500">{chore.points} pts</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {(editingGroup?.rotation_type !== 'present' && newGroup.rotation_type !== 'present') && (
                <div>
                  <label className="block text-sm font-medium mb-1">Kids in Rotation</label>
                  <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                    {kids.map(kid => (
                      <label key={kid.id} className="flex items-center mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedKids.includes(kid.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedKids([...selectedKids, kid.id])
                            } else {
                              setSelectedKids(selectedKids.filter(id => id !== kid.id))
                            }
                          }}
                          className="mr-2"
                        />
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2"
                          style={{ backgroundColor: kid.color_theme }}
                        >
                          {kid.display_name[0]}
                        </div>
                        <span className="text-sm">{kid.display_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  value={editingGroup ? editingGroup.start_date : newGroup.start_date}
                  onChange={(e) => editingGroup
                    ? setEditingGroup({ ...editingGroup, start_date: e.target.value })
                    : setNewGroup({ ...newGroup, start_date: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingGroup ? editingGroup.is_active : newGroup.is_active}
                    onChange={(e) => editingGroup
                      ? setEditingGroup({ ...editingGroup, is_active: e.target.checked })
                      : setNewGroup({ ...newGroup, is_active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm">Active</label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddGroup(false)
                  setEditingGroup(null)
                  setSelectedChores([])
                  setSelectedKids([])
                  setNewGroup({
                    name: '',
                    description: '',
                    chore_ids: [],
                    frequency: 'daily',
                    distribution_strategy: 'fair_rotation',
                    rotation_type: 'daily',
                    is_active: true,
                    auto_rotate: true,
                    kid_ids: []
                  })
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingGroup ? 'Save Changes' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}